import { PrimaryButton, RoundedButton } from '@components/Buttons';
import { Row } from '@components/Layout/Row';
import { useMediaQuery } from '@hooks/useMediaQuery';
import { IF } from '@packages/react/lib/if';
import React, { forwardRef, useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { InstantQuoteContext } from '../Stepper/InstantQuoteStepper';

const StyledFooter = styled(Row)`
    && {
        background-color: rgba(34, 34, 34, 0.9);
        backdrop-filter: blur(5px);
        padding: 20px 0;
        .submit-button {
            &-disabled {
                background-color: #eee;
            }
        }

        .skip-button {
            margin-right: 8px;
            color: #eee;

            &-disabled {
                color: #aaa;
            }
        }

        @media screen and (max-width: 600px) {
            & {
                height: 62px;
            }
            .submit-button,
            .skip-button {
                margin-bottom: 0;
            }
        }

        @media screen and (min-width: 600px) {
            .submit-button,
            .skip-button {
                margin-right: 5px;
            }
        }
    }
`;

interface IQFooterProps {
    currentStep: any;
    isSubmit: any;
    onClickBack: (step: any) => any;
    onClickNext: (step: any) => any;
}

const BUTTON_NEXT_TEXT = 'Next';
const BUTTON_BACK_TEXT = 'Back';
const BUTTON_SUBMIT_TEXT = 'Submit';

const DELAYED_HANDLER = (
    action: 'back' | 'next',
    loading: boolean,
    setLoading: (value: boolean) => void,
    onClick: (step: number) => void,
    props: IQFooterProps,
) => {
    if (!loading) {
        setLoading(true);

        setTimeout(() => {
            setLoading(false);

            if (action === 'back') {
                onClick(props.currentStep.idx - 1);
            } else if (action === 'next') {
                onClick(props.currentStep.idx + 1);
            }
        }, 500);
    }
};

const IQFooter = forwardRef<any, IQFooterProps>((props, ref) => {
    const [loadingNext, setLoadingNext] = useState(false);
    const [loadingBack, setLoadingBack] = useState(false);

    const [buttonNextDisabled, setButtonNextDisabled] = useState(true);
    const [buttonBackDisabled, setButtonBackDisabled] = useState(true);

    const [textNext, setTextNext] = useState(BUTTON_NEXT_TEXT);
    const [textBack] = useState(BUTTON_BACK_TEXT);

    const { instantQuote } = useContext(InstantQuoteContext);
    const { items } = instantQuote.order;

    const handleOnClickNext = () => {
        DELAYED_HANDLER('next', loadingNext, setLoadingNext, props.onClickNext, props);
    };

    const handleOnClickBack = () => {
        DELAYED_HANDLER('back', loadingBack, setLoadingBack, props.onClickBack, props);
    };

    useEffect(() => {
        switch (props.currentStep.route) {
            case 'category':
                setButtonBackDisabled(true);
                setButtonNextDisabled(!instantQuote.category_id);

                setTextNext(BUTTON_NEXT_TEXT);
                break;
            case 'summary':
                setButtonBackDisabled(false);
                setButtonNextDisabled(items.length === 0);

                setTextNext(BUTTON_NEXT_TEXT);
                break;
            case 'review':
                setButtonBackDisabled(false);
                setButtonNextDisabled(items.length === 0);

                setTextNext(BUTTON_SUBMIT_TEXT);
                break;
            default:
        }
    }, [props.currentStep.route, instantQuote.category_id]);

    useEffect(() => {
        switch (props.currentStep.route) {
            case 'review':
            case 'summary':
                setButtonNextDisabled(items.length === 0);
                break;
            default:
        }
    }, [items.length]);

    const isMobile = useMediaQuery('max-960');

    return (
        <StyledFooter padding={14} align={'center'} justify={'center'} ref={ref}>
            {IF(
                !isMobile,
                <RoundedButton
                    variant={'outlined'}
                    color={'white'}
                    disabled={buttonBackDisabled || loadingBack}
                    loading={loadingBack}
                    onClick={handleOnClickBack}
                    radius={3}
                    className={'submit-button'}
                    classes={{ disabled: 'submit-button-disabled' }}
                >
                    {textBack}
                </RoundedButton>,
            )}

            <PrimaryButton
                disabled={buttonNextDisabled || loadingNext}
                loading={loadingNext}
                onClick={handleOnClickNext}
                className={'submit-button'}
                classes={{ disabled: 'submit-button-disabled' }}
                fullWidth={isMobile}
            >
                {textNext}
            </PrimaryButton>
        </StyledFooter>
    );
});

export default IQFooter;

// <Typography variant={ 'body1' }> Questions? </Typography>
// <Typography variant={ 'body1' }>
//     <a href="tel:1-347-850-2720">
//         <Icon style={ { fontSize: 14 } }>phone</Icon>
//         1-347-850-2720
//     </a>
// </Typography>
